import type { OrbitController } from './orbitController';

/** Ab dieser Bewegung gilt ein Auswahl-Tipp als Orbit-Ziehen (px). */
const TAP_SLOP_PX = 6;
/** Dauer für Long-Press → Kontextmenü (ms). */
const LONG_PRESS_MS = 550;
/** Maximale Bewegung, die noch als Long-Press zählt (px). */
const LONG_PRESS_SLOP_PX = 8;
/** Zoomfaktor pro Gesture-Event begrenzen (rabiate Sprünge glätten). */
const ZOOM_FACTOR_MIN = 0.5;
const ZOOM_FACTOR_MAX = 2;

export interface TouchNavHost {
  camera: OrbitController;
  /** ID des aktiven Werkzeugs ('select', 'orbit', 'pan', 'zoom', …). */
  activeToolId(): string;
  forwardToolDown(e: PointerEvent): void;
  forwardToolMove(e: PointerEvent): void;
  forwardToolUp(e: PointerEvent): void;
  /** Laufenden Werkzeug-Stroke verwerfen (zweiter Finger, Kontextmenü). */
  abortTool(): void;
  openContextMenu(clientX: number, clientY: number): void;
  ndcAt(clientX: number, clientY: number): { x: number; y: number };
  requestUpdate(): void;
}

type TouchMode =
  | 'idle'    // nichts zu tun
  | 'tool'    // ein Finger steuert das aktive Werkzeug (Zeichnen, Kamera-Werkzeuge)
  | 'pending' // Auswahl-Werkzeug: Tippen = auswählen, Ziehen = Orbit
  | 'orbit'   // ein Finger dreht die Kamera
  | 'nav2';   // zwei Finger: Schwenken + Pinch-Zoom

interface TouchPoint {
  x: number;
  y: number;
  startX: number;
  startY: number;
}

/**
 * Touch-Navigation für die Arbeitsfläche (pointerType 'touch'):
 *  - Ein Finger steuert das aktive Werkzeug; mit Auswahl-Werkzeug wird
 *    Ziehen zum Orbit (um den Punkt unter dem Finger) und Tippen wählt aus.
 *  - Zwei Finger schwenken (Centroid) und zoomen (Pinch) gleichzeitig —
 *    unabhängig vom aktiven Werkzeug.
 *  - Long-Press öffnet das Kontextmenü (mobiles Äquivalent zum Rechtsklick).
 * Ein zweiter Finger bricht einen laufenden Werkzeug-Stroke sauber ab.
 */
export class TouchNavigator {
  private pointers = new Map<number, TouchPoint>();
  private mode: TouchMode = 'idle';
  private longPressTimer: number | null = null;
  private longPressFired = false;
  private lastCentroid = { x: 0, y: 0 };
  private lastPinchDist = 0;

  constructor(private host: TouchNavHost) {}

  /** true, solange die Kamera durch Touch bewegt wird (z. B. Presence-Cursor pausieren). */
  get isNavigating(): boolean {
    return this.mode === 'orbit' || this.mode === 'nav2';
  }

  /** true, solange ein Finger ins aktive Werkzeug weitergeleitet wird. */
  get isForwardingTool(): boolean {
    return this.mode === 'tool' || this.mode === 'pending';
  }

  pointerDown(e: PointerEvent): void {
    if (e.pointerType !== 'touch') return;
    e.preventDefault();
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, startX: e.clientX, startY: e.clientY });
    if (this.pointers.size > 1) {
      this.startNav();
      return;
    }
    this.longPressFired = false;
    if (this.host.activeToolId() === 'select') {
      // Tippen wählt aus, Ziehen dreht die Kamera — darum zuerst ans Werkzeug
      // durchreichen und bei überschrittener Schwelle in Orbit übergehen.
      this.mode = 'pending';
      this.host.forwardToolDown(e);
      this.armLongPress(e.clientX, e.clientY);
      return;
    }
    this.mode = 'tool';
    this.host.forwardToolDown(e);
  }

  pointerMove(e: PointerEvent): void {
    const t = this.pointers.get(e.pointerId);
    if (!t) return;
    const prevX = t.x;
    const prevY = t.y;
    t.x = e.clientX;
    t.y = e.clientY;

    switch (this.mode) {
      case 'nav2':
        this.gestureMove();
        return;
      case 'pending':
        if (this.longPressFired) return;
        if (Math.hypot(e.clientX - t.startX, e.clientY - t.startY) > TAP_SLOP_PX) {
          this.cancelLongPress();
          this.host.abortTool(); // Auswahl-Rahmen verwerfen
          this.mode = 'orbit';
          const ndc = this.host.ndcAt(t.startX, t.startY);
          this.host.camera.beginOrbit(ndc.x, ndc.y);
        }
        return;
      case 'orbit':
        this.host.camera.orbit(e.clientX - prevX, e.clientY - prevY);
        this.host.requestUpdate();
        return;
      case 'tool':
        this.host.forwardToolMove(e);
        return;
      default:
        return;
    }
  }

  pointerUp(e: PointerEvent): void {
    const t = this.pointers.get(e.pointerId);
    if (!t) return;
    this.pointers.delete(e.pointerId);

    switch (this.mode) {
      case 'nav2':
        // Wieder zwei Finger übrig → Geste neu anfassen; sonst Navigation beenden
        if (this.pointers.size === 2) this.beginGesture();
        else if (this.pointers.size < 2) this.endNav();
        return;
      case 'orbit':
        this.host.camera.endOrbit();
        this.mode = 'idle';
        return;
      case 'pending':
        this.cancelLongPress();
        this.mode = 'idle';
        if (!this.longPressFired) this.host.forwardToolUp(e); // Tap = Auswahl
        return;
      case 'tool':
        this.mode = 'idle';
        this.host.forwardToolUp(e);
        return;
      default:
        this.mode = 'idle';
    }
  }

  /** Zeiger abgebrochen (System-Geste, Anruf): alles sauber zurücksetzen. */
  pointerCancel(e: PointerEvent): void {
    if (!this.pointers.delete(e.pointerId)) return;
    this.cancelLongPress();
    if (this.mode === 'orbit') this.host.camera.endOrbit();
    if (this.mode === 'tool' || this.mode === 'pending') this.host.abortTool();
    this.mode = 'idle';
    this.lastPinchDist = 0;
  }

  // ---------- Internas ----------

  /** Zweiter Finger: laufenden Stroke verwerfen und Zwei-Finger-Navigation starten. */
  private startNav(): void {
    this.cancelLongPress();
    if (this.mode === 'tool' || this.mode === 'pending') this.host.abortTool();
    if (this.mode === 'orbit') this.host.camera.endOrbit();
    this.mode = 'nav2';
    this.beginGesture();
  }

  private endNav(): void {
    this.mode = 'idle';
    this.lastPinchDist = 0;
  }

  /** Geste an den ersten zwei Fingern neu verankern (Pan-Griffpunkt + Pinch-Basis). */
  private beginGesture(): void {
    const [a, b] = this.firstTwo();
    if (!a || !b) return;
    this.lastCentroid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    this.lastPinchDist = Math.hypot(b.x - a.x, b.y - a.y);
    const ndc = this.host.ndcAt(this.lastCentroid.x, this.lastCentroid.y);
    this.host.camera.beginPan(ndc.x, ndc.y);
  }

  private gestureMove(): void {
    const [a, b] = this.firstTwo();
    if (!a || !b || this.lastPinchDist <= 0) return;
    const cx = (a.x + b.x) / 2;
    const cy = (a.y + b.y) / 2;
    const d = Math.hypot(b.x - a.x, b.y - a.y);
    // Pinch = Zoom zum Geste-Mittelpunkt, Centroid-Bewegung = Schwenken
    const factor = Math.min(ZOOM_FACTOR_MAX, Math.max(ZOOM_FACTOR_MIN, d / this.lastPinchDist));
    if (factor !== 1) {
      const ndc = this.host.ndcAt(cx, cy);
      this.host.camera.zoomAt(factor, ndc.x, ndc.y);
    }
    this.host.camera.pan(cx - this.lastCentroid.x, cy - this.lastCentroid.y);
    this.lastCentroid = { x: cx, y: cy };
    this.lastPinchDist = d;
    this.host.requestUpdate();
  }

  private firstTwo(): [TouchPoint | undefined, TouchPoint | undefined] {
    const it = this.pointers.values();
    const a = it.next().value as TouchPoint | undefined;
    const b = it.next().value as TouchPoint | undefined;
    return [a, b];
  }

  private armLongPress(startX: number, startY: number): void {
    this.cancelLongPress();
    this.longPressTimer = window.setTimeout(() => {
      this.longPressTimer = null;
      this.longPressFired = true;
      this.host.abortTool();
      this.host.openContextMenu(startX, startY);
      navigator.vibrate?.(10);
    }, LONG_PRESS_MS);
  }

  private cancelLongPress(): void {
    if (this.longPressTimer !== null) {
      window.clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }
}
